import { useRef, useState, useEffect } from "react";
import Marquee from "react-fast-marquee";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  CalendarClock,
  Smartphone,
  MapPin,
  Magnet,
  MousePointerClick,
  PhoneCall,
  Upload,
  Rocket,
  Users,
  LineChart,
  GraduationCap,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useContact } from "@/App";
import { scrollToId } from "@/lib/lenis";

const LOGO =
  "https://customer-assets.emergentagent.com/job_b6b60f97-07a0-4683-9511-7792f33deeb2/artifacts/2v97hnjc_2023-rich-g-logo.png";
const ICON =
  "https://customer-assets.emergentagent.com/job_b6b60f97-07a0-4683-9511-7792f33deeb2/artifacts/ge7fpwm3_2023-rich-g-faviconlogo.png";

const IMG = {
  hero: "https://static.prod-images.emergentagent.com/jobs/b6b60f97-07a0-4683-9511-7792f33deeb2/images/d70b8f68abbe884cf700607fd6609b553e979a13e1c64730c5f1f85129ff28db.jpeg",
  about:
    "https://customer-assets-7cd3h4nn.emergentagent.net/job_richg-launch/artifacts/mefgxu15_Richard-Glover.jpg",
  mockTrainer:
    "https://static.prod-images.emergentagent.com/jobs/b6b60f97-07a0-4683-9511-7792f33deeb2/images/87e6932bb2618099f8c9829992a2b9d515b9a9f8e242f5b3a0f406e363758acc.jpeg",
  mockMobile:
    "https://static.prod-images.emergentagent.com/jobs/b6b60f97-07a0-4683-9511-7792f33deeb2/images/20c1db4ba49037db77bc907ffc58f54815a432ef4a0bc42fdac0c1e5101d1ced.jpeg",
  mockYoga:
    "https://static.prod-images.emergentagent.com/jobs/b6b60f97-07a0-4683-9511-7792f33deeb2/images/6b1b4624a65e892a4cf205fc4278c7c37e51210bb6be62278e0a5f6af955a8af.jpeg",
  mockNutrition:
    "https://static.prod-images.emergentagent.com/jobs/b6b60f97-07a0-4683-9511-7792f33deeb2/images/8496778b57ba1e0f11c0da5ac9e36130a578bf07c60a1b71ae530a0ea4d599c1.jpeg",
  mbbc: "/mbbc-home.jpg",
};

/* Reveal-on-view via IntersectionObserver + CSS transition (with fallback). */
function useReveal(rootMargin = "-60px") {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            io.disconnect();
          }
        });
      },
      { rootMargin }
    );
    io.observe(el);
    const fallback = setTimeout(() => setShown(true), 2000);
    return () => {
      io.disconnect();
      clearTimeout(fallback);
    };
  }, [rootMargin]);
  return [ref, shown];
}

const Reveal = ({ children, delay = 0, className = "" }) => {
  const [ref, shown] = useReveal();
  return (
    <div
      ref={ref}
      className={`reveal-item ${shown ? "is-in" : ""} ${className}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
};

const Chapter = ({ n, label }) => (
  <Reveal className="flex items-center gap-4 mb-8">
    <span className="font-serif text-ember text-lg">{n}</span>
    <span className="h-px w-10 bg-navy/25" />
    <span className="text-xs uppercase tracking-[0.25em] text-navy/60">{label}</span>
  </Reveal>
);

/* ---------- Nav ---------- */
const NAV_LINKS = [
  { label: "The offer", id: "offer" },
  { label: "Process", id: "process" },
  { label: "Latest", id: "latest" },
  { label: "Examples", id: "gallery" },
  { label: "About", id: "about" },
  { label: "FAQ", id: "faq" },
];

const Nav = () => {
  const { openContact } = useContact();
  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div className="mx-auto max-w-7xl px-5 md:px-10 mt-3 md:mt-4">
        <div className="flex items-center justify-between rounded-full border border-navy/10 bg-cream/70 backdrop-blur-xl px-4 md:px-6 py-2.5 shadow-[0_4px_30px_rgba(38,58,92,0.06)]">
          <button
            onClick={() => scrollToId("top")}
            data-testid="nav-logo"
            className="flex items-center"
            aria-label="Rich G Design home"
          >
            <img src={LOGO} alt="Rich G Design" className="h-8 md:h-9 w-auto" />
          </button>

          <nav className="hidden md:flex items-center gap-5 lg:gap-7">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => scrollToId(l.id)}
                className="text-sm text-navy/70 hover:text-navy transition-colors"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <button
            onClick={() => openContact("A free 20-minute call")}
            data-testid="nav-cta-button"
            className="group inline-flex items-center gap-2 rounded-full bg-navy px-4 md:px-5 py-2.5 text-sm text-cream transition-[background-color,transform] hover:bg-navy-deep active:scale-95"
          >
            Book a call
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>
    </header>
  );
};

/* ---------- Hero ---------- */
const HERO_LINES = [
  { text: "Websites for" },
  { text: "personal trainers," },
  { text: "nutritionists &" },
  { text: "wellbeing coaches.", accent: true },
];

const Hero = () => {
  const { openContact } = useContact();
  const [h1Ref, h1Shown] = useReveal();
  return (
    <section id="top" className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden">
      <img
        src={ICON}
        alt=""
        aria-hidden
        className="pointer-events-none absolute -right-16 -top-10 w-72 md:w-[26rem] opacity-[0.05] rotate-12"
      />
      <div className="mx-auto max-w-7xl px-5 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-6 items-center">
        <div className="lg:col-span-6">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-navy/15 bg-white/60 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-navy/70">
              <span className="h-1.5 w-1.5 rounded-full bg-ember" />
              Web design for wellbeing pros
            </span>
          </Reveal>

          <h1
            ref={h1Ref}
            className={`mt-6 font-serif font-medium text-navy tracking-tight text-[2.7rem] leading-[1.06] sm:text-6xl lg:text-[4.6rem] ${
              h1Shown ? "is-in" : ""
            }`}
          >
            {HERO_LINES.map((l, i) => (
              <span key={i} className="line-mask pb-[0.12em] -mb-[0.07em]">
                <span
                  className={`line-inner ${l.accent ? "text-ember" : ""}`}
                  style={{ transitionDelay: `${0.05 + i * 0.1}s` }}
                >
                  {l.text}
                </span>
              </span>
            ))}
          </h1>

          <Reveal delay={0.15}>
            <p className="mt-7 max-w-xl text-lg text-navy/70 leading-relaxed">
              Done-for-you websites from{" "}
              <span className="text-navy font-medium">£750</span> — plus custom apps when you
              need more. Live in two weeks, with no tech headaches — just a professional
              presence that gets you booked.
            </p>
          </Reveal>

          <Reveal delay={0.28} className="mt-9 flex flex-col sm:flex-row gap-3 sm:items-center">
            <button
              onClick={() => openContact("The Practitioner Site — £750")}
              data-testid="hero-cta-button"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-ember px-7 py-4 text-cream font-medium transition-[background-color,transform,box-shadow] hover:bg-ember/90 hover:shadow-xl active:scale-[0.98]"
            >
              Get your site
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollToId("process")}
              data-testid="hero-secondary-button"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-navy/20 px-6 py-4 text-navy transition-colors hover:bg-navy hover:text-cream"
            >
              How it works
            </button>
          </Reveal>
        </div>

        <div className="lg:col-span-6 relative">
          <Reveal delay={0.2}>
            <div className="relative aspect-[4/5] leaf-shadow lg:scale-[1.14] lg:origin-center">
              <img
                src={IMG.hero}
                alt="A wellbeing website shown on a laptop and phone"
                className="leaf-frame h-full w-full object-cover"
              />
            </div>
          </Reveal>
          <div className="absolute -left-4 md:-left-8 bottom-8 rounded-2xl bg-navy text-cream px-5 py-4 shadow-xl grain overflow-hidden">
            <p className="relative z-10 font-serif text-3xl leading-none">14</p>
            <p className="relative z-10 text-xs uppercase tracking-widest text-cream/70 mt-1">
              days to a live website
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Marquee ---------- */
const MARQUEE = [
  "Personal Trainers",
  "Nutritionists",
  "Yoga Teachers",
  "Wellbeing Coaches",
  "Pilates Studios",
  "Sports Therapists",
  "Health Practitioners",
];

const Ticker = () => (
  <section className="py-8 border-y border-navy/10 bg-sand/60">
    <Marquee speed={38} gradient={false} autoFill>
      {MARQUEE.map((m) => (
        <span key={m} className="flex items-center">
          <span className="font-serif text-2xl md:text-3xl text-navy/80 px-10">{m}</span>
          <span className="h-1.5 w-1.5 rounded-full bg-ember" />
        </span>
      ))}
    </Marquee>
  </section>
);

/* ---------- Problem ---------- */
const Problem = () => (
  <section className="py-24 md:py-36">
    <div className="mx-auto max-w-4xl px-5 md:px-10">
      <Chapter n="01" label="The problem" />
      <Reveal>
        <p className="font-serif text-2xl sm:text-3xl md:text-4xl leading-[1.35] text-navy tracking-tight">
          You're brilliant at what you do — but your website doesn't show it. A DIY site you
          built at midnight, a social page doing a job it was never meant to do, or nothing at
          all.{" "}
          <span className="text-navy/50">
            Every week without a proper site is a week of clients choosing someone who looks
            more established than you. Not better. Just more visible.
          </span>
        </p>
      </Reveal>
    </div>
  </section>
);

/* ---------- Offer ---------- */
const FEATURES = [
  { icon: MousePointerClick, t: "Built to convert", d: "A 4–5 page site (home, about, services, testimonials, contact) designed around one goal: turning visitors into booked clients." },
  { icon: CalendarClock, t: "Online booking", d: "Integrated scheduling so clients book in without the endless back-and-forth." },
  { icon: Magnet, t: "Lead magnet opt-in", d: "Connected to your email list, so your site grows your audience even while you sleep." },
  { icon: Smartphone, t: "Mobile-first design", d: "Because that's where your clients actually are when they find you." },
  { icon: MapPin, t: "Local SEO basics", d: "Set up properly, so people searching \u201Cpersonal trainer near me\u201D actually find you." },
];

const Offer = () => {
  const { openContact } = useContact();
  return (
    <section id="offer" className="py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <Chapter n="02" label="The offer" />
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <Reveal>
              <div className="rounded-3xl bg-navy text-cream p-8 md:p-10 relative overflow-hidden grain">
                <div className="relative z-10">
                  <p className="text-xs uppercase tracking-[0.25em] text-cream/60">
                    One flagship package
                  </p>
                  <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-tight">
                    The Practitioner Site
                  </h2>
                  <div className="mt-6 flex items-end gap-3">
                    <span className="font-serif text-6xl text-ember">£750</span>
                    <span className="mb-2 text-cream/70">live in 14 days</span>
                  </div>
                  <p className="mt-5 text-cream/70 leading-relaxed">
                    Built specifically for wellbeing professionals. You send me your content and
                    photos. Fourteen days later, you're live.
                  </p>
                  <button
                    onClick={() => openContact("A free 20-minute call")}
                    data-testid="offer-cta-button"
                    className="group mt-8 inline-flex items-center gap-2 rounded-full bg-ember px-6 py-3.5 text-cream font-medium transition-[background-color,transform,box-shadow] hover:bg-ember/90 hover:shadow-xl active:scale-[0.98]"
                  >
                    Book a free 20-minute call
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </button>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-5">
              {FEATURES.map((f, i) => (
                <Reveal key={f.t} delay={i * 0.05}>
                  <div className="group h-full rounded-2xl border border-navy/10 bg-white/60 p-6 transition-[transform,box-shadow] hover:-translate-y-1 hover:shadow-[0_20px_40px_-24px_rgba(38,58,92,0.4)]">
                    <div className="h-11 w-11 rounded-xl bg-sand flex items-center justify-center text-navy transition-colors group-hover:bg-ember group-hover:text-cream">
                      <f.icon className="h-5 w-5" strokeWidth={1.7} />
                    </div>
                    <h3 className="mt-4 font-serif text-xl text-navy">{f.t}</h3>
                    <p className="mt-2 text-sm text-navy/60 leading-relaxed">{f.d}</p>
                  </div>
                </Reveal>
              ))}
              <Reveal delay={0.1}>
                <div
                  id="careplan"
                  className="h-full rounded-2xl border border-ember/30 bg-ember/5 p-6 flex flex-col justify-between"
                >
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-ember">The care plan</p>
                    <h3 className="mt-3 font-serif text-xl text-navy leading-snug">
                      Never think about your website again
                    </h3>
                    <p className="mt-2 text-sm text-navy/60 leading-relaxed">
                      Hosting, security, backups & small changes whenever you need them. Email me
                      and it's done.
                    </p>
                  </div>
                  <p className="mt-4 font-serif text-2xl text-navy">
                    £39<span className="text-base text-navy/50">/month</span>
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- How it works ---------- */
const STEPS = [
  { n: "01", icon: PhoneCall, t: "The kickoff call", d: "A relaxed 20-minute chat about your practice, your clients, and exactly what you want your website to do for you." },
  { n: "02", icon: Upload, t: "You send your content", d: "Your words and photos — I'll guide you on both. Then I handle the design, build, copy polish, booking and local SEO." },
  { n: "03", icon: Rocket, t: "You go live", d: "Fourteen days later your site is live: mobile-first, fast, and built to turn visitors into booked clients." },
];

const Process = () => (
  <section id="process" className="py-24 md:py-32 bg-sand/40 border-y border-navy/10">
    <div className="mx-auto max-w-7xl px-5 md:px-10">
      <Chapter n="03" label="How it works" />
      <Reveal>
        <h2 className="font-serif text-4xl md:text-5xl text-navy tracking-tight max-w-2xl leading-tight">
          From hello to live in three simple steps.
        </h2>
      </Reveal>
      <div className="mt-14 grid md:grid-cols-3 gap-6 lg:gap-8">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.08}>
            <div className="group relative h-full rounded-3xl border border-navy/10 bg-cream p-8 transition-[transform,box-shadow] hover:-translate-y-1 hover:shadow-[0_24px_50px_-28px_rgba(38,58,92,0.45)]">
              <div className="flex items-center justify-between">
                <span className="font-serif text-5xl text-navy/15 leading-none">{s.n}</span>
                <div className="h-12 w-12 rounded-2xl bg-navy flex items-center justify-center text-cream transition-colors group-hover:bg-ember">
                  <s.icon className="h-5 w-5" strokeWidth={1.7} />
                </div>
              </div>
              <h3 className="mt-6 font-serif text-2xl text-navy">{s.t}</h3>
              <p className="mt-3 text-navy/65 leading-relaxed">{s.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- Latest project ---------- */
const MBBC_FEATURES = [
  "Client member area — teaching videos, recipes & PDF guides",
  "Therapist dashboard — classes, venues, earnings & QR sign-ups",
  "Three membership tiers with online sign-up",
  "Data-led brand around BIA body-composition, fully mobile-first",
];

const LatestProject = () => (
  <section id="latest" className="py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-5 md:px-10">
      <Chapter n="04" label="Latest project" />
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <Reveal>
            <div className="rounded-2xl border border-navy/10 bg-white shadow-[0_30px_70px_-30px_rgba(38,58,92,0.45)] overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 bg-sand border-b border-navy/10">
                <span className="h-3 w-3 rounded-full bg-ember/70" />
                <span className="h-3 w-3 rounded-full bg-navy/20" />
                <span className="h-3 w-3 rounded-full bg-navy/20" />
                <div className="ml-3 flex-1 rounded-full bg-cream px-3 py-1 text-xs text-navy/50 truncate">
                  mybestbodyclub.com
                </div>
              </div>
              <div className="mbbc-frame relative h-[460px] overflow-hidden bg-white group/preview">
                <img
                  src={IMG.mbbc}
                  alt="My Best Body Club homepage, built by Rich G Design"
                  className="mbbc-scroll w-full block"
                />
                <span className="pointer-events-none absolute bottom-3 right-3 rounded-full bg-navy/80 text-cream text-[11px] tracking-wide px-3 py-1 backdrop-blur transition-opacity duration-300 group-hover/preview:opacity-0">
                  Hover to scroll ↓
                </span>
              </div>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-5 order-1 lg:order-2">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-ember">Nutrition & wellbeing</p>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl text-navy tracking-tight leading-tight">
              My Best Body Club
            </h2>
            <p className="mt-5 text-navy/70 leading-relaxed">
              Andrea Wood, a qualified Nutritional Therapist, wanted to move the conversation
              beyond the scales. I built her a complete platform around BIA body-composition data
              and whole-food nutrition — a membership site for clients and a management dashboard
              for therapists.
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <ul className="mt-6 space-y-3">
              {MBBC_FEATURES.map((f) => (
                <li key={f} className="flex gap-3 text-navy/80">
                  <Check className="h-5 w-5 text-ember shrink-0 mt-0.5" strokeWidth={2} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.16}>
            <a
              href="https://mybestbodyclub.com"
              target="_blank"
              rel="noreferrer"
              data-testid="latest-live-link"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3.5 text-cream font-medium transition-[background-color,transform] hover:bg-navy-deep active:scale-[0.98]"
            >
              Visit the live site
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

/* ---------- What you get (gallery) ---------- */
const SHOTS = [
  { img: IMG.mockTrainer, label: "Personal trainer", span: "lg:col-span-7", ratio: "aspect-[16/10]" },
  { img: IMG.mockMobile, label: "Booking, mobile-first", span: "lg:col-span-5", ratio: "aspect-[4/5]" },
  { img: IMG.mockYoga, label: "Yoga & classes", span: "lg:col-span-5", ratio: "aspect-[4/5]" },
  { img: IMG.mockNutrition, label: "Nutrition coach", span: "lg:col-span-7", ratio: "aspect-[16/10]" },
];

const Gallery = () => (
  <section id="gallery" className="py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-5 md:px-10">
      <Chapter n="05" label="What you get" />
      <Reveal>
        <h2 className="font-serif text-4xl md:text-5xl text-navy tracking-tight max-w-2xl leading-tight">
          A glimpse of the kind of site you'll get.
        </h2>
      </Reveal>
      <Reveal delay={0.05}>
        <p className="mt-4 text-navy/60 max-w-xl leading-relaxed">
          Every build is designed around your brand and your clients — but here's the look,
          feel and quality you can expect.
        </p>
      </Reveal>
      <div className="mt-14 grid lg:grid-cols-12 gap-6 lg:gap-8 items-start">
        {SHOTS.map((s, i) => (
          <Reveal key={i} delay={(i % 2) * 0.08} className={s.span}>
            <figure className="group relative overflow-hidden rounded-3xl border border-navy/10 bg-sand shadow-[0_20px_50px_-30px_rgba(38,58,92,0.5)] transition-[transform,box-shadow] hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(38,58,92,0.55)]">
              <div className={`w-full ${s.ratio} overflow-hidden`}>
                <img
                  src={s.img}
                  alt={s.label}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
                />
              </div>
              <figcaption className="absolute left-4 bottom-4 rounded-full bg-cream/90 backdrop-blur px-4 py-1.5 text-xs uppercase tracking-widest text-navy">
                {s.label}
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- App development ---------- */
const APP_TYPES = [
  { icon: Users, t: "Client portals & membership areas" },
  { icon: CalendarClock, t: "Booking & scheduling systems" },
  { icon: LineChart, t: "Coaching & progress-tracking apps" },
  { icon: GraduationCap, t: "Online courses & content platforms" },
];

const AppServices = () => {
  const { openContact } = useContact();
  return (
    <section id="apps" className="py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="relative overflow-hidden rounded-[2rem] bg-navy grain p-8 md:p-14">
          <img src={ICON} alt="" aria-hidden className="pointer-events-none absolute -right-10 -bottom-12 w-64 opacity-[0.06]" />
          <div className="relative z-10 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-6">
              <p className="text-xs uppercase tracking-[0.25em] text-ember">Beyond websites</p>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl text-cream tracking-tight leading-tight">
                Need more than a website? I build apps, too.
              </h2>
              <p className="mt-5 text-cream/70 leading-relaxed max-w-lg">
                Sometimes a practice outgrows a simple site. I design and build custom web apps
                for wellbeing professionals — the same hand-coded, AI-accelerated craft, tailored
                to how you actually work with clients.
              </p>
              <button
                onClick={() => openContact("A custom app or web app")}
                data-testid="apps-cta-button"
                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-ember px-6 py-3.5 text-cream font-medium transition-[background-color,transform,box-shadow] hover:bg-ember/90 hover:shadow-xl active:scale-[0.98]"
              >
                Tell me about your idea
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
            <div className="lg:col-span-6">
              <div className="grid sm:grid-cols-2 gap-4">
                {APP_TYPES.map((a, i) => (
                  <Reveal key={a.t} delay={i * 0.06}>
                    <div className="group h-full rounded-2xl border border-cream/15 bg-cream/[0.04] p-5 transition-colors hover:bg-cream/[0.08]">
                      <div className="h-11 w-11 rounded-xl bg-cream/10 flex items-center justify-center text-ember transition-colors group-hover:bg-ember group-hover:text-cream">
                        <a.icon className="h-5 w-5" strokeWidth={1.7} />
                      </div>
                      <h3 className="mt-4 font-serif text-lg text-cream leading-snug">{a.t}</h3>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- About ---------- */
const About = () => (
  <section id="about" className="py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-6 order-2 lg:order-1">
        <Reveal>
          <div className="relative aspect-[4/5] leaf-shadow">
            <div className="leaf-frame absolute inset-0 bg-sand">
              <img src={IMG.about} alt="Richard, founder of Rich G Design" className="h-full w-full object-contain" />
            </div>
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-6 order-1 lg:order-2">
        <Chapter n="06" label="About" />
        <Reveal>
          <h2 className="font-serif text-4xl md:text-5xl text-navy tracking-tight leading-tight">
            Hi, I'm Richard.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-6 space-y-5 text-lg text-navy/70 leading-relaxed max-w-2xl">
            <p>
              I've been designing websites since 2014, and I specialise in one thing: sites for
              wellbeing practitioners. Personal trainers, nutritionists, yoga teachers, coaches —
              people who transform their clients' lives but haven't had time to build an online
              presence that reflects it.
            </p>
            <p>
              I combine hand-coded craft with the latest AI-assisted tools, which means you get a
              site that would have taken months and cost thousands a few years ago — delivered in
              two weeks, at a price that makes sense for a growing practice.
            </p>
            <p className="text-navy">
              <MapPin className="inline h-5 w-5 text-ember mr-1 -mt-1" />
              Based in Bradford, West Yorkshire — working with practitioners across the UK.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------- FAQ ---------- */
const FAQS = [
  { q: "What do you need from me?", a: "Your words, your photos, and about an hour of your time on a kickoff call. I handle everything else." },
  { q: "What if I don't have professional photos or copy?", a: "Most of my clients don't. I'll guide you on simple phone photography that works, and help shape your words into copy that sells." },
  { q: "Do I own my website?", a: "Yes, completely. The care plan is optional — if you ever want to take your site elsewhere, it goes with you." },
  { q: "Can you add online payments, a shop or a members area later?", a: "Yes. The Practitioner Site is built to grow with you, and extras can be added anytime." },
];

const Faq = () => (
  <section id="faq" className="py-24 md:py-32 bg-sand/40 border-y border-navy/10">
    <div className="mx-auto max-w-4xl px-5 md:px-10">
      <Chapter n="07" label="Questions" />
      <Reveal>
        <h2 className="font-serif text-4xl md:text-5xl text-navy tracking-tight mb-10">
          Good to know.
        </h2>
      </Reveal>
      <Reveal delay={0.05}>
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-navy/15">
              <AccordionTrigger
                data-testid={`faq-trigger-${i}`}
                className="text-left font-serif text-xl md:text-2xl text-navy hover:no-underline hover:text-ember py-6"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-navy/70 text-base leading-relaxed pb-6">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

/* ---------- Final CTA ---------- */
const FinalCta = () => {
  const { openContact } = useContact();
  return (
    <section className="py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="relative overflow-hidden rounded-[2rem] bg-navy grain px-6 py-20 md:px-16 md:py-28 text-center">
          <img src={ICON} alt="" aria-hidden className="pointer-events-none absolute -left-10 -bottom-10 w-64 opacity-[0.07]" />
          <Reveal>
            <h2 className="relative z-10 font-serif text-4xl sm:text-5xl md:text-6xl text-cream tracking-tight leading-[1.05] max-w-3xl mx-auto">
              Ready to look as good online as you are{" "}
              <span className="text-ember">in person?</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="relative z-10 mt-6 text-cream/70 text-lg max-w-xl mx-auto">
              Book a free 20-minute call. No pressure, no jargon — just a chat about what your
              practice needs.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <button
              onClick={() => openContact("A free 20-minute call")}
              data-testid="final-cta-button"
              className="group relative z-10 mt-10 inline-flex items-center gap-2 rounded-full bg-ember px-8 py-4 text-cream font-medium transition-[background-color,transform,box-shadow] hover:bg-ember/90 hover:shadow-2xl active:scale-[0.98]"
            >
              Book your call
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ---------- Footer ---------- */
const Footer = () => (
  <footer className="border-t border-navy/10 py-12">
    <div className="mx-auto max-w-7xl px-5 md:px-10 flex flex-col md:flex-row items-center justify-between gap-6">
      <div className="flex items-center gap-3">
        <img src={ICON} alt="Rich G Design" className="h-10 w-auto" />
        <div className="text-sm text-navy/60">
          <p className="text-navy font-medium">Rich G Design</p>
          <p>Bradford, West Yorkshire</p>
        </div>
      </div>
      <a
        href="mailto:rich@richgdesign.com"
        data-testid="footer-email-link"
        className="text-navy hover:text-ember transition-colors font-serif text-lg"
      >
        rich@richgdesign.com
      </a>
      <p className="text-xs text-navy/40">
        © {new Date().getFullYear()} Rich G Design · Since 2012
      </p>
    </div>
  </footer>
);

export default function Landing() {
  return (
    <main className="relative">
      <Nav />
      <Hero />
      <Ticker />
      <Problem />
      <Offer />
      <Process />
      <LatestProject />
      <Gallery />
      <AppServices />
      <About />
      <Faq />
      <FinalCta />
      <Footer />
    </main>
  );
}
